// Port of BivouacEngine/SleepTonightModels.swift (SleepTonightRequest, OvernightOption, OvernightGroup, PlannerWarning,
// SleepTonightResult).
//
// Every type here is plain, structured-cloneable data, so a result computed in a Web Worker can be posted to the page
// as is.

import type {
  Availability,
  BivouacCandidate,
  BivouacReason,
  Confidence,
  DataFreshness,
  DataProvenance,
  EpochMs,
  GeoCoordinate,
  GeoPolygon,
  OvernightCategory,
  OvernightPreference,
  PointOfInterest,
  RegulatoryState,
  SuitabilityStatus,
  ThunderstormRisk,
  UserReport,
  WeatherSnapshot,
} from '../core/models';

/** Input of `SleepTonightPlanner.plan` with every default applied. */
export interface SleepTonightRequest {
  /** Where the hiker is (or the chosen origin). */
  position: GeoCoordinate;
  /** Hiker elevation (m), if known. */
  elevation?: number;
  /** Current time (injected; never read from the system clock by the engine). */
  now: EpochMs;
  /** When the hiker wants to stop walking; civil dusk when absent. */
  desiredStopTime?: EpochMs;
  /** Maximum additional walking time (seconds). Default 3 h. */
  maxAdditionalWalking: number;
  /** Maximum estimated walking detour (m, one way) from the route to an option. Default 1000. */
  maxDetourMeters: number;
  /** Preferred way to spend the night (orders the result groups). Default `mixed`. */
  preference: OvernightPreference;
  /** Whether options already passed are included. Default true. */
  includeOptionsBehind: boolean;
  /** How far back (m along the route) options behind are considered. Default 3000. */
  behindLimitMeters: number;
  /** User observations; `regulationSignObserved` reports add caution to bivouac search areas and shelters. */
  userReports: UserReport[];
}

/** What a caller has to provide: `position` and `now`; the rest takes the Swift defaults. */
export type SleepTonightRequestInit = Pick<SleepTonightRequest, 'position' | 'now'> & Partial<SleepTonightRequest>;

/** Applies the Swift `SleepTonightRequest.init` defaults. */
export function makeSleepTonightRequest(init: SleepTonightRequestInit): SleepTonightRequest {
  const request: SleepTonightRequest = {
    position: init.position,
    now: init.now,
    maxAdditionalWalking: init.maxAdditionalWalking ?? 3 * 3600,
    maxDetourMeters: init.maxDetourMeters ?? 1000,
    preference: init.preference ?? 'mixed',
    includeOptionsBehind: init.includeOptionsBehind ?? true,
    behindLimitMeters: init.behindLimitMeters ?? 3000,
    userReports: init.userReports ?? [],
  };
  if (init.elevation !== undefined) request.elevation = init.elevation;
  if (init.desiredStopTime !== undefined) request.desiredStopTime = init.desiredStopTime;
  return request;
}

/** One place where the night could be spent: an official facility, a shelter or a bivouac search area. */
export interface OvernightOption {
  /** `poi:<poi id>` for facilities, the candidate id (`zone:<lat>:<lon>`) for search areas. */
  id: string;
  /** Sleep Tonight category (the group the option is shown in). */
  category: OvernightCategory;
  /** POI name; absent for bivouac search areas (UI labels them). */
  name?: string;
  /** Source POI (facilities and shelters). */
  poi?: PointOfInterest;
  /** Source candidate (bivouac search areas). */
  candidate?: BivouacCandidate;
  /** POI location, or the generalised centre of a search area. */
  coordinate: GeoCoordinate;
  /** Search area polygon (search areas only). */
  area?: GeoPolygon;
  /** Signed distance along the route from the hiker's projection: negative = behind the hiker. */
  distanceAlongRoute: number;
  /** Estimated one-way walking detour from the route (straight-line distance × 1.3). */
  detourMeters: number;
  /** Total walking distance (return to the route if off-route + along the route + detour). */
  totalDistance: number;
  /** Ascent (m) along the route plus the climb of the detour(s) when elevations are known. */
  ascent: number;
  /** Descent (m) along the route plus the descent of the detour(s) when elevations are known. */
  descent: number;
  /** Walking time in seconds (personal factor applied, breaks excluded). */
  estimatedDuration: number;
  /** `now + estimatedDuration`. */
  estimatedArrival: EpochMs;
  /** The option lies behind the hiker along the route (`distanceAlongRoute < 0`). */
  isBehind: boolean;
  /** Estimated arrival is after sunset. */
  arrivesAfterSunset: boolean;
  /** Estimated arrival is after the requested stop time. */
  arrivesAfterDesiredStop: boolean;
  /** Forecast at arrival, temperature adjusted to the option's elevation when known. */
  forecastAtArrival?: WeatherSnapshot;
  /** Distance (m) to the nearest water POI, if one is within 2 km. */
  nearestWaterDistance?: number;
  /** `official` only for designated facilities; never for search areas. */
  regulatoryState: RegulatoryState;
  /** Confidence in `regulatoryState`. */
  regulatoryConfidence: Confidence;
  /** Confidence in the underlying place data (POI record or terrain grid); `low` when synthetic. */
  sourceConfidence: Confidence;
  /** Whether the place's data is available offline (from the caller's availability function). */
  availability: Availability;
  /** Presentation status (regulation always overrides convenience). */
  status: SuitabilityStatus;
  /** Machine-readable explanation (terrain, regulation, weather, access, data quality). */
  reasons: BivouacReason[];
  /** Ranking key within a group (higher is better): status, then arrival before dusk, then effort, then confidence. */
  rankScore: number;
}

/** Ranked options of one category (one group per category; the category is the group's id). */
export interface OvernightGroup {
  category: OvernightCategory;
  /** Best first. */
  options: OvernightOption[];
}

/** Plan-level caveats (the app layer renders localized text). */
export type PlannerWarning =
  /** The hiker is more than 250 m from the route. */
  | { kind: 'offRoute'; distance: number }
  /** Less than 90 minutes of daylight left. */
  | { kind: 'lateInDay'; minutesToSunset: number }
  /** The sun has already set. */
  | { kind: 'afterSunset' }
  /** Thunderstorm risk ≥ moderate between now and the end of the night. */
  | { kind: 'thunderstormRisk'; risk: ThunderstormRisk }
  /** Gusts ≥ 60 km/h between now and the end of the night. */
  | { kind: 'strongWind'; gustKmh: number }
  /** Mean wind ≥ 40 km/h (`strongMeanWindKmh`) when the forecast has no gusts (gusts can be stronger). */
  | { kind: 'strongMeanWind'; kmh: number }
  /** Night temperature below 0 °C (adjusted to the highest option elevation when known). */
  | { kind: 'coldNight'; celsius: number }
  /** Forecast older than the weather freshness policy's "aging" limit (expired forecasts are not used). */
  | { kind: 'staleWeather'; fetchedAt: EpochMs }
  /** No usable forecast for the planning window. */
  | { kind: 'noWeather' }
  /** Some of the data (places, terrain, regulations or weather) is synthetic demo data. */
  | { kind: 'syntheticData' }
  /** No option that is not `unsuitable` can be reached within the time budget. */
  | { kind: 'noOptionsWithinReach' }
  /** The hiker is more than 3 km from the route: route-based estimates are unreliable. */
  | { kind: 'outsideRouteCorridor' };

/** Output of `SleepTonightPlanner`. */
export interface SleepTonightResult {
  /** `request.now`. */
  generatedAt: EpochMs;
  /** The request this result answers (defaults applied). */
  request: SleepTonightRequest;
  /** Hiker's projection on the route (m). */
  distanceAlong: number;
  /** Hiker's distance from the route (m). */
  crossTrackDistance: number;
  /** Sunset at the hiker's position (offline solar computation; terrain shading not modelled). */
  sunset?: EpochMs;
  /** Civil dusk (default stop time). */
  civilDusk?: EpochMs;
  /** Time budget in seconds: min(desiredStopTime ?? civil dusk, now + maxAdditionalWalking) − now, never negative. */
  availableWalkingTime: number;
  /** Ordered by preference.preferredCategories then remaining; empty groups omitted. */
  groups: OvernightGroup[];
  /** Plan-level caveats. */
  warnings: PlannerWarning[];
  /** Places, terrain, regulations or weather contain synthetic demo data. */
  usesSyntheticData: boolean;
  /** When the forecast used was fetched (always shown to the hiker). */
  weatherFetchedAt?: EpochMs;
  /** Freshness of that forecast at `generatedAt`. */
  weatherFreshness?: DataFreshness;
  /** Provenance of that forecast (its licence credit is shown next to it). */
  weatherProvenance?: DataProvenance;
}

/** All options, group by group (Swift `SleepTonightResult.allOptions`). */
export function allOptions(result: SleepTonightResult): OvernightOption[] {
  return result.groups.flatMap((g) => g.options);
}
